import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Button, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Translate, getTranslation } from "orion-components/i18n";
import { accessManagementService, activityService, timeConversion } from "client-app-core";
import { TargetingIcon, getIconByTemplate } from "orion-components/SharedComponents";
import { useDispatch, useSelector } from "react-redux";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import PersonIcon from "@mui/icons-material/Person";
import { getAllDeviceAlarmMappings } from "orion-components/GlobalData/Actions";
import { loadProfile } from "orion-components/ContextPanel/Actions";

const propTypes = {
	cardholder: PropTypes.object,
	activityType: PropTypes.string,
	filter: PropTypes.string,
	alertDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	dir: PropTypes.string
};

const defaultProps = {
	cardholder: {},
	activityType: "",
	filter: "",
	alertDateTime: "",
	dir: "ltr"
};

const PROFILE_ENABLED_APPS = new Set(["map-app", "events-app", "cameras-app", "facilities-app", "replay-app"]);

const getAccessControlTypeKey = activityType =>
	activityType?.startsWith("access-control.") ? activityType.replace("access-control.", "") : "";

const getActivityTypeLabel = (activityType, deviceAlarmMappings) => {
	if (!activityType) return "";

	const activityTypeKey = getAccessControlTypeKey(activityType);
	if (activityTypeKey && deviceAlarmMappings?.[activityTypeKey]?.alarmTypeName) {
		return deviceAlarmMappings[activityTypeKey].alarmTypeName;
	}

	return "";
};

const getSummaryStatusLabel = (activityType, filter, deviceAlarmMappings) => {
	if (activityType?.startsWith("access-control.")) {
		return getActivityTypeLabel(activityType, deviceAlarmMappings) || filter?.alarmTypeName || "";
	}

	if (filter?.alarmTypeName) {
		return filter.alarmTypeName;
	}

	if (typeof filter === "string" && filter) {
		return deviceAlarmMappings?.[filter]?.alarmTypeName || "";
	}

	return "";
};

const getHistoryEventTypeLabel = (activity, deviceAlarmMappings) => {
	const activityTypeLabel = getActivityTypeLabel(activity?.type, deviceAlarmMappings);
	if (activityTypeLabel) {
		return activityTypeLabel;
	}

	return getAccessControlTypeKey(activity?.type) || "";
};

const formatTimestamp = (timestamp, timeFormatPreference = "12-hour", locale = "en") => {
	if (!timestamp) return "";

	return timeConversion.convertToUserTime(timestamp, `full_${timeFormatPreference}`, locale) || timestamp;
};

const getDeviceName = activity => {
	return (
		activity?.target?.name ||
		activity?.target?.entityData?.properties?.name ||
		activity?.target?.entity?.entityData?.properties?.name ||
		""
	);
};

const getDeviceIcon = (activity, deviceTypes) => {
	const target = activity?.target || {};
	const entityData = target?.entityData || target?.entity?.entityData;
	const profileIconTemplate = entityData?.properties?.profileIconTemplate || null;
	const deviceTypeId = target?.deviceType;
	const deviceTypeIcon = deviceTypeId ? deviceTypes?.[deviceTypeId]?.icon : null;
	if (deviceTypeIcon) {
		return <SpriteIcon iconName={deviceTypeIcon} customWidth="20px" />;
	}
	const type = entityData?.properties?.type || entityData?.properties?.deviceType || target?.entityType || "device";

	return getIconByTemplate(type, { ...target, entityData }, "1.35rem", profileIconTemplate);
};

const mapActivitiesToHistory = (activities, deviceTypes, deviceAlarmMappings, timeFormatPreference, locale) =>
	activities.map(activity => ({
		id: activity.id,
		deviceId: activity?.target?.id,
		deviceFeedId: activity?.target?.feedId,
		deviceEntityType: activity?.target?.entityType || activity?.target?.type || "device",
		deviceGeometry:
			activity?.target?.entityData?.geometry ||
			activity?.target?.geometry ||
			activity?.target?.entity?.entityData?.geometry ||
			null,
		deviceName: getDeviceName(activity),
		deviceIcon: getDeviceIcon(activity, deviceTypes),
		eventType: getHistoryEventTypeLabel(activity, deviceAlarmMappings),
		timestamp: formatTimestamp(activity.published || activity.activityDate, timeFormatPreference, locale),
		published: activity.published || activity.activityDate || ""
	}));

const getCardholderName = cardholder => {
	const nameParts = [cardholder?.firstName, cardholder?.middleName, cardholder?.lastName].filter(Boolean);
	return nameParts.length ? nameParts.join(" ") : cardholder?.name || "John Smith";
};

const getCardholderRole = (cardholder, cardholderTypes) => {
	const subtype = cardholder?.subtype;
	if (!subtype) {
		return cardholder?.rank || "";
	}

	return (
		cardholderTypes?.[subtype]?.name ||
		Object.values(cardholderTypes || {}).find(type => type?.name === subtype)?.name ||
		subtype ||
		cardholder?.rank ||
		""
	);
};

const getInitials = name =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part.charAt(0).toUpperCase())
		.join("") || "JS";

const getCardholderImageCandidates = cardholder => {
	const accessResourceImage = cardholder?.imageUrl
		? `/_fileDownload?bucketName=ac-resource-images&fileName=${cardholder.imageUrl}`
		: "";
	const imageHandle = cardholder?.handle || cardholder?.profileImage;
	const directSource = cardholder?.image;

	const candidates = [];

	if (accessResourceImage) {
		candidates.push(accessResourceImage);
	}

	if (imageHandle) {
		candidates.push(
			`/_download?handle=${imageHandle}-thumbnail`,
			`/_download?handle=${imageHandle}-thumbnail&timestamp=${Date.now()}`,
			`/_download?handle=${imageHandle}`
		);
	}

	if (directSource) {
		candidates.push(directSource);
	}

	return candidates;
};

const BadgeHistoryWidget = ({ cardholder, activityType, filter, alertDateTime, dir }) => {
	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState("summary");
	const [historyActivities, setHistoryActivities] = useState([]);
	const [loadMore, setLoadMore] = useState(true);
	const [imageLoadFailed, setImageLoadFailed] = useState(false);
	const [imageAttemptIndex, setImageAttemptIndex] = useState(0);
	const [cardholderTypes, setCardholderTypes] = useState({});
	const locale = useSelector(state => state?.i18n?.locale || "en");
	const timeFormatPreference = useSelector(state => state?.appState?.global?.timeFormat || "12-hour");
	const appId = useSelector(state => state?.application?.appId || state?.appId || "");
	const deviceTypes = useSelector(state => state?.globalData?.deviceTypes ?? {});
	const deviceAlarmMappings = useSelector(state => state?.globalData?.deviceAlarmMappings ?? {});
	const canOpenDeviceProfile = PROFILE_ENABLED_APPS.has(appId);

	const fetchHistory = toDate => {
		if (!cardholder?.id) return;
		activityService.getCardholderAccessHistory(cardholder.id, 5, toDate, (err, res) => {
			if (err) {
				console.log(err);
				return;
			}

			if (res) {
				setHistoryActivities(prev => {
					const existingIds = new Set(prev.map(item => item.id));
					const nextItems = res.filter(item => !existingIds.has(item.id));
					return [...prev, ...nextItems];
				});
				if (res.length < 5) {
					setLoadMore(false);
				}
			}
		});
	};

	useEffect(() => {
		if (!cardholder?.id) return;

		setHistoryActivities([]);
		setLoadMore(true);
		fetchHistory(alertDateTime || new Date().toISOString());
	}, [cardholder?.id, alertDateTime]);

	useEffect(() => {
		if (Object.keys(deviceAlarmMappings).length === 0) {
			dispatch(getAllDeviceAlarmMappings());
		}
	}, [deviceAlarmMappings, dispatch]);

	useEffect(() => {
		accessManagementService.getCardHolderTypes((err, response) => {
			if (err || !Array.isArray(response)) {
				console.error("Fetch cardholder types error", err || response);
				return;
			}

			setCardholderTypes(
				response.reduce((acc, type) => {
					acc[type.id] = {
						id: type.id,
						name: type.value
					};
					return acc;
				}, {})
			);
		});
	}, []);

	useEffect(() => {
		setImageLoadFailed(false);
		setImageAttemptIndex(0);
	}, [cardholder?.image, cardholder?.imageUrl, cardholder?.profileImage, cardholder?.handle]);

	const historyItems = useMemo(
		() => mapActivitiesToHistory(historyActivities, deviceTypes, deviceAlarmMappings, timeFormatPreference, locale),
		[historyActivities, deviceTypes, deviceAlarmMappings, timeFormatPreference, locale]
	);
	const summaryName = getCardholderName(cardholder);
	const summaryId = cardholder?.sourceId || cardholder?.id || "123456789";
	const summaryAlarmMapping = typeof filter === "string" ? deviceAlarmMappings?.[filter] : filter;

	const summary = {
		id: summaryId,
		name: summaryName,
		role: getCardholderRole(cardholder, cardholderTypes),
		status: getSummaryStatusLabel(activityType, summaryAlarmMapping || filter, deviceAlarmMappings)
	};
	const summaryImageCandidates = useMemo(() => getCardholderImageCandidates(cardholder), [cardholder]);
	const summaryImageSrc = imageLoadFailed ? "" : summaryImageCandidates[imageAttemptIndex] || "";

	const containerClassName = `badge-history-widget ${activeTab === "summary" ? "summary-active" : "history-active"}`;
	const summaryLayoutStyle = {
		flexDirection: dir === "rtl" ? "row-reverse" : "row"
	};
	const statusValueStyle = {
		color:
			summary.status.toLowerCase().startsWith("valid") || summary.status.toLowerCase().startsWith("granted")
				? "#7CD992"
				: "#E85858"
	};

	const handleLoadDeviceProfile = item => {
		if (!canOpenDeviceProfile || !item?.deviceId) return;

		dispatch(loadProfile(item.deviceId, item.deviceName, item.deviceEntityType, "profile", "secondary"));
	};

	return (
		<div className={containerClassName} data-testid="badge-history-widget">
			<div className="badge-history-widget__title" data-testid="badge-history-widget-header">
				<Translate value="global.profiles.widgets.badgeHistory.title" />
			</div>

			<div className="badge-history-widget__tabs">
				<Tabs
					value={activeTab}
					onChange={(event, nextTab) => setActiveTab(nextTab)}
					variant="standard"
					TabIndicatorProps={{ className: "MuiTabs-indicator" }} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tabs"
				>
					<Tab
						disableRipple
						value="summary"
						label={getTranslation("global.profiles.widgets.badgeHistory.summary")}
						className="badge-history-widget__tab badge-history-widget__tab--summary" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tab"
					/>
					<Tab
						disableRipple
						value="history"
						label={getTranslation("global.profiles.widgets.badgeHistory.history")}
						className="badge-history-widget__tab badge-history-widget__tab--history" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tab-2"
					/>
				</Tabs>
			</div>

			{activeTab === "summary" ? (
				<div className="badge-history-widget__summary" style={summaryLayoutStyle}>
					<div className="badge-history-widget__image-frame">
						{summaryImageSrc ? (
							<img
								alt={`badge-history-${summary.id}`}
								src={summaryImageSrc}
								className="badge-history-widget__image"
								onError={() => {
									if (imageAttemptIndex < summaryImageCandidates.length - 1) {
										setImageAttemptIndex(prev => prev + 1);
										return;
									}
									setImageLoadFailed(true);
								}} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-img"
							/>
						) : (
							<Avatar className="badge-history-widget__avatar-fallback">
								{summary.name ? (
									<span className="badge-history-widget__avatar-initials">
										{getInitials(summary.name)}
									</span>
								) : (
									<PersonIcon className="badge-history-widget__avatar-icon" />
								)}
							</Avatar>
						)}
					</div>
					<div className="badge-history-widget__summary-details">
						<div>
							<Typography className="badge-history-widget__label">
								{getTranslation("global.profiles.widgets.cardHolder.status")}
							</Typography>
							<Typography className="badge-history-widget__value" style={statusValueStyle}>
								{summary.status}
							</Typography>
						</div>
						<div>
							<Typography className="badge-history-widget__label">
								{getTranslation("global.profiles.widgets.cardHolder.name")}
							</Typography>
							<Typography className="badge-history-widget__value">{summary.name}</Typography>
						</div>
						<div>
							<Typography className="badge-history-widget__label">
								{getTranslation("global.profiles.widgets.cardHolder.id")}
							</Typography>
							<Tooltip title={`#${summary.id}`} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tooltip">
								<Typography className="badge-history-widget__value badge-history-widget__value--ellipsis">
									#{summary.id}
								</Typography>
							</Tooltip>
						</div>
						<div>
							<Typography className="badge-history-widget__label">
								{getTranslation("global.profiles.widgets.badgeHistory.role")}
							</Typography>
							<Typography className="badge-history-widget__value">{summary.role}</Typography>
						</div>
					</div>
				</div>
			) : (
				<div className="badge-history-widget__history-panel" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-div">
					<div className="badge-history-widget__table-header" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-div-2">
						<Typography className="badge-history-widget__table-header-text">
							{getTranslation("global.profiles.widgets.badgeHistory.device")}
						</Typography>
						<Typography className="badge-history-widget__table-header-text">
							{getTranslation("global.profiles.widgets.badgeHistory.eventType")}
						</Typography>
						<Typography className="badge-history-widget__table-header-text">
							{getTranslation("global.profiles.widgets.badgeHistory.dateAndTime")}
						</Typography>
					</div>
					{historyItems.map(item => (
						<div key={item.id} className="badge-history-widget__table-row" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-div-3">
							<div className="badge-history-widget__first-column">
								{item.deviceGeometry ? (
									<div className="badge-history-widget__icon-wrap badge-history-widget__icon-wrap--targeting">
										<TargetingIcon
											id={item.deviceId}
											feedId={item.deviceFeedId}
											geometry={item.deviceGeometry}
											defaultVisibility={true}
											iconContainerStyle={{ padding: 0, marginTop: 2 }}
										/>
									</div>
								) : null}
								<div className="badge-history-widget__icon-wrap">
									<span className="badge-history-widget__device-icon">{item.deviceIcon}</span>
								</div>
								<Tooltip title={item.deviceName} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tooltip-2">
									<span className="badge-history-widget__cell-tooltip-target">
										{canOpenDeviceProfile ? (
											<Button
												disableRipple
												className="badge-history-widget__device-name badge-history-widget__device-name--link"
												onClick={() => handleLoadDeviceProfile(item)} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-button"
											>
												<span className="badge-history-widget__ellipsis">
													{item.deviceName}
												</span>
											</Button>
										) : (
											<Typography className="badge-history-widget__device-name">
												<span className="badge-history-widget__ellipsis">
													{item.deviceName}
												</span>
											</Typography>
										)}
									</span>
								</Tooltip>
							</div>
							<Tooltip title={item.eventType} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tooltip-3">
								<span className="badge-history-widget__cell-tooltip-target">
									<Typography className="badge-history-widget__row-text badge-history-widget__ellipsis">
										{item.eventType}
									</Typography>
								</span>
							</Tooltip>
							<Tooltip title={item.timestamp} data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-tooltip-4">
								<span className="badge-history-widget__cell-tooltip-target">
									<Typography className="badge-history-widget__row-text badge-history-widget__ellipsis">
										{item.timestamp}
									</Typography>
								</span>
							</Tooltip>
						</div>
					))}
					{loadMore && cardholder?.id && (
						<div className="badge-history-widget__load-more-wrap">
							<Button
								disableRipple
								onClick={() => fetchHistory(historyActivities[historyActivities.length - 1]?.published)}
								className="badge-history-widget__load-more-button" data-testid="orion-components-profiles-widgets-badge-history-badge-history-widget-button-2"
							>
								<Translate value="global.profiles.widgets.badgeHistory.loadMore" />
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

BadgeHistoryWidget.propTypes = propTypes;
BadgeHistoryWidget.defaultProps = defaultProps;

export default BadgeHistoryWidget;
